<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="profileMetadata?.isMissingTaxDetails"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <WithSkeletonLoader :is-loading="pending">
      <RevInfoBlock
        v-if="shouldDisplayInfoBlock"
        class="mb-12 mt-24"
        :content="i18n(translations.onboardedInfoBlockContent)"
        :icon="IconWarning"
        :title="i18n(translations.onboardedInfoBlockTitle)"
        variant="warning"
      />
      <div v-if="shouldDisplayEmptyState">
        <div class="text-action-default-low body-1 mb-16">
          {{ i18n(translations.taxDetailsEmptyStateHeader) }}
        </div>

        <div class="mb-16 flex items-center">
          <IconArrowRight />
          <div class="body-1 ml-12">
            {{ i18n(translations.euSellersAction) }}
          </div>
        </div>
        <div class="mb-16 flex items-center">
          <IconArrowRight />
          <div class="body-1 ml-12">
            {{ i18n(translations.notEuSellersAction) }}
          </div>
        </div>
      </div>
      <div v-if="!shouldDisplayEmptyState && vatInformations">
        <div class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.legalAreaLabel) }}
          </h4>
          <p>
            {{ legalAreaFormattedValue }}
          </p>
        </div>
        <div v-if="vatInformations.expeditionCountry" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.shippingFrom) }}
          </h4>
          <p>
            {{ i18n.country(vatInformations.expeditionCountry) }}
          </p>
        </div>
        <div v-if="vatInformations.taxIdNumber" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.taxIdNumberLabel) }}
          </h4>
          <p class="body-1 flex items-center justify-between">
            {{ vatInformations.taxIdNumber }}
          </p>
        </div>
        <div v-if="vatInformations.intraCommunityVatNumber" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.intracommunityVatNumberLabel) }}
          </h4>
          <p class="body-1 flex items-center">
            {{ vatInformations.intraCommunityVatNumber }}
            <RevTag
              v-if="vatInformations.vatNumberCheckStatus.label"
              class="ml-8"
              :label="vatInformations.vatNumberCheckStatus.label"
              :variant="
                getStatusTagVariant(vatInformations.vatNumberCheckStatus.code)
              "
            />
          </p>
          <RevInfoBlock
            v-if="vatInformations.vatNumberCheckStatus.message"
            class="my-12 block"
            :icon="getStatusIcon(vatInformations.vatNumberCheckStatus.code)"
            :title="vatInformations.vatNumberCheckStatus.message"
            :variant="
              getStatusInfoVariant(vatInformations.vatNumberCheckStatus.code)
            "
          />
        </div>
        <div v-if="vatInformations.isExemptFromVat" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.isExemptFromVatLabel) }}
          </h4>
          <p class="body-1 flex items-center justify-between">
            {{ i18n(translations.yes) }}
          </p>
        </div>
        <div v-if="vatInformations.vatMarginScheme" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.vatMarginSchemeLabel) }}
          </h4>
          <p class="body-1 flex items-center justify-between">
            {{
              i18n(
                FORMATTED_VALUES.VAT_MARGIN_SCHEME[
                  vatInformations.vatMarginScheme
                ],
              )
            }}
          </p>
        </div>
        <div v-if="shouldDisplayOssStatus" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.ossRegistrationLabel) }}
          </h4>
          <p class="body-1 flex items-center justify-between">
            {{ ossStatusLabel }}
          </p>
        </div>
        <div v-if="vatInformations.vatNumbersByCountry?.length" class="mb-16">
          <h4 class="caption mb-4">
            {{ i18n(translations.vatNumbersByCountryLabel) }}
          </h4>
          <div
            v-for="country in vatInformations.vatNumbersByCountry"
            :key="country.market"
            class="body-1 mb-8 grid w-1/2 grid-cols-3 gap-8"
          >
            <div>
              {{ i18n.country(country.market) }}
            </div>
            <div>
              {{ country.vatNumber }}
            </div>
          </div>
        </div>
      </div>
      <template v-if="profileMetadata">
        <RevButton
          v-if="profileMetadata.isMissingTaxDetails"
          variant="primary"
          @click="handleOpenTaxDetailsModal"
        >
          {{ i18n(translations.addTaxDetails) }}
        </RevButton>
        <RevButton
          v-if="!profileMetadata.isMissingTaxDetails"
          :icon="IconEdit"
          variant="secondary"
          with-icon
          @click="handleOpenTaxDetailsModal"
        >
          {{ i18n(translations.edit) }}
        </RevButton>
        <TaxDetailsModal
          v-if="profileMetadata"
          :expedition-countries="profileMetadata.expeditionCountries"
          :oss-documents
          :vat-informations
          @update-vat-informations="handleVatInformationUpdated"
        />
      </template>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed } from 'vue'

import { MarketplaceCode, sellerProfileAPI } from '@backmarket/http-api'
import type { GetProfileMetadata } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { IconArrowRight } from '@ds/icons/IconArrowRight'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconWarning } from '@ds/icons/IconWarning'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { MODAL_NAMES } from '../../../../Profile.constants'
import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import { FORMATTED_VALUES } from './TaxDetails.constants'
import translations from './TaxDetails.translations'
import {
  getStatusIcon,
  getStatusInfoVariant,
  getStatusTagVariant,
} from './TaxDetails.utils'
import TaxDetailsModal from './components/TaxDetailsModal/TaxDetailsModal.vue'

const MODAL_NAME = MODAL_NAMES.EDIT_TAX_DETAILS

const props = defineProps<{
  profileMetadata: GetProfileMetadata | null
}>()

const emit = defineEmits(['update-tax-details'])

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const { onboardingCompleted } = useSellerInfoStore()

const [
  {
    data: vatInformations,
    error: vatInformationsError,
    pending: vatInformationsPending,
    refresh: vatInformationsRefresh,
  },
  {
    data: ossDocuments,
    error: ossDocumentsError,
    pending: ossDocumentsPending,
    refresh: ossDocumentsRefresh,
  },
] = await Promise.all([
  useHttpLazyFetch(sellerProfileAPI.getVatInformations),
  useHttpLazyFetch(sellerProfileAPI.getLegalDocuments, {
    queryParams: { type: 'oss_registration' },
  }),
])

const pending = computed(
  () => vatInformationsPending.value || ossDocumentsPending.value,
)
const error = computed(
  () => vatInformationsError.value || ossDocumentsError.value,
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const ossStatusLabel = computed(() =>
  i18n(
    vatInformations.value?.isOssRegistered ? translations.yes : translations.no,
  ),
)
const legalAreaFormattedValue = computed(() =>
  i18n(
    vatInformations.value?.legalArea === MarketplaceCode.EU
      ? translations.legalAreaEuFormattedValue
      : translations.legalAreaNotEuFormattedValue,
  ),
)
const shouldDisplayEmptyState = computed(
  () => !vatInformations.value?.legalArea,
)
const shouldDisplayInfoBlock = computed(
  () => props.profileMetadata?.isMissingTaxDetails && onboardingCompleted,
)
const shouldDisplayOssStatus = computed(
  () => vatInformations.value?.isOssRegistered !== null,
)

function refresh() {
  vatInformationsRefresh()
  ossDocumentsRefresh()
}

function handleOpenTaxDetailsModal() {
  openModal(MODAL_NAME)
}

function handleVatInformationUpdated() {
  refresh()
  emit('update-tax-details')
}
</script>
